import type { AnnualAdjustments, AnnualAllowances, AnnualNonFinancials, Class4NicsExemptionReason } from '../types'

// Field-label groups for the two AnnualSummary financial sub-objects (BE9b/BE6) — keys
// mirror the exact JSON property names of AnnualAdjustments/AnnualAllowances (camelCase of
// the C# AnnualAdjustments/AnnualAllowances models), used both to render the manual entry
// form and to build FiguresReviewTable rows on the review step.
export type FigureField = { key: string; label: string }

export const ADJUSTMENT_FIELDS: FigureField[] = [
  { key: 'includedNonTaxableProfits', label: 'Included non-taxable profits' },
  { key: 'basisAdjustment', label: 'Basis adjustment' },
  { key: 'accountingAdjustment', label: 'Accounting adjustment' },
  { key: 'averagingAdjustment', label: 'Averaging adjustment' },
  { key: 'outstandingBusinessIncome', label: 'Outstanding business income' },
  { key: 'balancingChargeBpra', label: 'Balancing charge (BPRA)' },
  { key: 'balancingChargeOther', label: 'Balancing charge (other)' },
  { key: 'goodsAndServicesOwnUse', label: 'Goods and services for own use' }
]

export const ALLOWANCE_FIELDS: FigureField[] = [{ key: 'tradingIncomeAllowance', label: 'Trading income allowance' }]

// HMRC's class4NicsExemptionReason enum (any other value: FORMAT_CLASS_4_EXEMPTION_REASON), with
// plain-English labels. '' is the "no exemption" choice and is sent as null.
export const CLASS_4_NICS_EXEMPTION_OPTIONS: { value: Class4NicsExemptionReason | ''; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'non-resident', label: 'Non-resident' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'diver', label: 'Diver' },
  { value: 'ITTOIA-2005', label: 'Taxed under ITTOIA 2005' },
  { value: 'over-state-pension-age', label: 'Over State Pension age' },
  { value: 'under-16', label: 'Under 16' }
]

export const getClass4NicsExemptionLabel = (value: string | null | undefined): string =>
  CLASS_4_NICS_EXEMPTION_OPTIONS.find((option) => option.value === (value || ''))?.label ?? value ?? 'None'

export const OBLIGATION_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Open', label: 'Open' },
  { value: 'Fulfilled', label: 'Fulfilled' }
] as const

// The HMRC OAuth setup screen (FE5) — same path SetupModule.tsx itself expects
// (location.state.tokenExpired), mirroring QuarterlySubmissionModule's reconnect-prompt
// navigation.
export const HMRC_SETUP_ROUTE_PATH = '/supervisor/financial-setup/hmrc-itsa-setup'

// Every AnnualSummary field defaults to zero/null — there is no annual Calculate
// (ADR 0031) to pre-populate them from the ledger; the user enters every figure manually.
export const buildZeroAdjustments = (): AnnualAdjustments =>
  Object.fromEntries(ADJUSTMENT_FIELDS.map(({ key }) => [key, 0])) as unknown as AnnualAdjustments

export const buildZeroAllowances = (): AnnualAllowances =>
  Object.fromEntries(ALLOWANCE_FIELDS.map(({ key }) => [key, 0])) as unknown as AnnualAllowances

export const buildInitialNonFinancials = (): AnnualNonFinancials => ({
  class4NicsExemptionReason: null
})

// Extracts the server-supplied message from an axios error thrown by ospreyAPI.
// OspreyExceptionMiddleware (every Osprey .NET API) returns `{ StatusCode, Message }` on a
// non-2xx response — `message` (lowercase) is also checked defensively, mirroring the
// established defensive-extraction pattern elsewhere in the app (see
// modules/document-bundles CreateDocumentBundles.tsx, QuarterlySubmissionModule/constants.ts).
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { Message?: string; message?: string } } })?.response
  return response?.data?.Message || response?.data?.message || (error as Error)?.message || fallback
}

// True when the API rejected the call with a 400 (ValidationException — e.g. the BE4
// ItsaFirmEligibilityGuard) as opposed to a 498 (HmrcTokenExpiredError, handled separately)
// or any other status.
export const isValidationError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 400

export const buildFigureRows = (fields: FigureField[], values: Record<string, number> | undefined | null) =>
  fields.map(({ key, label }) => ({ category: label, amount: values?.[key] ?? 0 }))
